import { isNullOrUndefined } from "../../../../../../../tools";
import { IdProvider } from "../../../../../../services/idprovider.service";
import { ServerCommunicatorService } from "../../../../../../services/server-communicator.service";
import { UIUtils } from "../../../../../../services/uiutils.service";
import { Utils } from "../../../../../../services/utils.service";
import { DataContainer } from "../../../../../infrastructure/datacontainer";
import { DataContainerService } from "../../../../../infrastructure/datacontainer.service";
import { RequestTypes } from "../../../../../infrastructure/enums";
import { IPersistable } from "../../../../../infrastructure/IPersistable";
import { PayloadPacketFacade } from "../../../../../infrastructure/payloadpacket/payloadpacketfacade";
import { TransportData } from "../../../../../infrastructure/transportdata";
import { ValidationResultAccumulator } from "../../../../../infrastructure/validationresultaccumulator";
import { ValidationMessages, ValidationPatterns } from "../../../../constants";
import { VillageFetchRequest } from "./villagefetchrequest";
import { VillageWithoutZoneFetchRequest } from "./villagewithoutzonefetchrequest";

export class VillageProps {

  public created_date: string = "";
  public updated_date: string = "";
  public created_by: number = 0;
  public updated_by: number = 0;

  public id: number = 0;
  public name_en: string = "";
  public name_mr: string = "";
  public state_id: number = 0;
  public readonly state_name_en: string = "";
  public readonly state_name_mr: string = "";
  public zone_id: number = 0;
  public readonly zone_name: string = "";
  public district_id: number = 0;
  public readonly district_name_en: string = "";
  public readonly district_name_mr: string = "";
  public block_id: number = 0;
  public readonly block_name_en: string = "";
  public readonly block_name_mr: string = "";
  public phc_id: number = 0;
  public readonly phc_name: string = "";
  public sc_id: number = 0;
  public readonly sc_name: string = "";
  public month: string = "";
  public financial_year_id: number = 0;

  public sanctioned_no_of_asha_in_village: number = 0;

  public readonly is_newly_created: boolean = false;

  private constructor(IsNewlyCreated: boolean) {
    this.is_newly_created = IsNewlyCreated;
  }

  public static Blank() {
    return new VillageProps(true);
  }
}

export class Village implements IPersistable<Village> {
  public static readonly mastertablename: string = "village_master";

  private constructor(
    public readonly p: VillageProps,
    public readonly AllowEdit: boolean,
  ) {}

  public async EnsurePrimaryKeysWithValidValues(): Promise<void> {
    if (this.p.id === undefined || this.p.id === 0) {
      const newids = await IdProvider.GetInstance().GetNextEntityId();
      // const newids = await IdProvider.GetInstance().GetAllocateSingleIds();
      this.p.id = newids[0];
      if (this.p.id <= 0)
        throw new Error("Cannot assign Id. Please try again");
    }
  }

  public GetEditableVersion(): Village {
    let newVillage: VillageProps = Utils.GetInstance().DeepCopy(this.p);
    return Village.CreateInstance(newVillage, true);
  }

  public static CreateNewInstance() {
    return new Village(VillageProps.Blank(), true);
  }

  public static CreateInstance(data: any, allowEdit: boolean) {
    return new Village(data as VillageProps, allowEdit);
  }

  public CheckSaveValidity(
    _td: TransportData,
    vra: ValidationResultAccumulator,
  ): void {
    if (!this.AllowEdit)
      vra.add("", "This object is not editable and hence cannot be saved.");
    if (!this.p.name_en || this.p.name_en.trim() == "") {
      vra.add("name_en", "Village Name cannot be blank. ");
    }
    if (this.p.block_id <= 0)
      vra.add("block_id", "Block Name cannot be Blank.");

    if (this.p.sc_id <= 0) vra.add("sc_id", "Sc Name cannot be Blank.");

    // Validate month (string but numeric)
    const monthNum = Number(this.p.month);
    if (!this.p.month || isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
      vra.add("month", "Please select a valid month.");
    }
    // ✔ Correct validation for compulsory + > 0

    if (
      !this.p.sanctioned_no_of_asha_in_village ||
      this.p.sanctioned_no_of_asha_in_village <= 0
    ) {
      vra.add(
        "sanctioned_no_of_asha_in_village",
        "Sanctioned No. of ASHA must be greater than 0.",
      );
    }
  }

  public MergeIntoTransportData(td: TransportData) {
    DataContainerService.GetInstance().MergeIntoContainer(
      td.MainData,
      Village.mastertablename,
      this.p,
    );
  }

  private static m_currentInstance: Village = Village.CreateNewInstance();

  public static GetCurrentInstance() {
    return Village.m_currentInstance;
  }

  public static SetCurrentInstance(value: Village) {
    Village.m_currentInstance = value;
  }

  // ********************************************
  public static cacheDataChangeLevel: number = -1;

  public static SingleInstanceFromTransportData(td: TransportData): Village {
    let dcs = DataContainerService.GetInstance();
    if (dcs.CollectionExists(td.MainData, Village.mastertablename)) {
      for (let data of dcs.GetCollection(td.MainData, Village.mastertablename)!
        .Entries) {
        return Village.CreateInstance(data, false);
      }
    }

    return null as any;
  }

  public static ListFromDataContainer(
    cont: DataContainer,
    filterPredicate: (arg0: any) => boolean = null as any,
    sortPropertyName: string = "Name",
  ): Village[] {
    let result: Village[] = [];

    let dcs = DataContainerService.GetInstance();

    if (dcs.CollectionExists(cont, Village.mastertablename)) {
      let coll = dcs.GetCollection(cont, Village.mastertablename)!;
      let entries = coll.Entries;

      if (!isNullOrUndefined(filterPredicate))
        entries = entries.filter(filterPredicate);

      if (sortPropertyName.trim().length > 0) {
        entries = Utils.OrderByPropertyName(entries, sortPropertyName);
      }

      for (let data of entries) {
        result.push(Village.CreateInstance(data, false));
      }
    }

    return result;
  }

  public static ListFromTransportData(td: TransportData): Village[] {
    return Village.ListFromDataContainer(td.MainData);
  }

  public static async FetchTransportData(
    req: VillageFetchRequest,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler,
  ) {
    let tdRequest = req.FormulateTransportData();
    let pktRequest =
      PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr =
      await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
      return null;
    }

    let tdResponse = JSON.parse(tr.Tag) as TransportData;
    return tdResponse;
  }

  public static async FetchInstance(
    id: number,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler,
  ) {
    let req = new VillageFetchRequest();
    req.village_ids.push(id);

    let tdResponse = (await Village.FetchTransportData(
      req,
      errorHandler,
    )) as TransportData;
    return Village.SingleInstanceFromTransportData(tdResponse);
  }

  public static async FetchList(
    req: VillageFetchRequest,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler,
  ) {
    let tdResponse = (await Village.FetchTransportData(
      req,
      errorHandler,
    )) as TransportData;
    return Village.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireList(
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler,
  ) {
    let req = new VillageFetchRequest();
    let tdResponse = (await Village.FetchTransportData(
      req,
      errorHandler,
    )) as TransportData;
    return Village.ListFromTransportData(tdResponse);
  }
  public static async FetchEntireListForSuperAdmin(
    role: number,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler,
  ) {
    let req = new VillageFetchRequest();
    req.role.push(role);
    let tdResponse = (await Village.FetchTransportData(
      req,
      errorHandler,
    )) as TransportData;
    return Village.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireListBySCRef(
    sc_id: number,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler,
  ) {
    let req = new VillageFetchRequest();
    if (sc_id) {
      req.sc_ids.push(sc_id);
    }
    let tdResponse = (await Village.FetchTransportData(
      req,
      errorHandler,
    )) as TransportData;
    return Village.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireListBySCRefs(
    sc_ids: number[],
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler,
  ) {
    let req = new VillageFetchRequest();
    req.sc_ids = sc_ids;
    let tdResponse = (await Village.FetchTransportData(
      req,
      errorHandler,
    )) as TransportData;
    return Village.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireListByBlockRef(
    block_id: number,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler,
  ) {
    let req = new VillageWithoutZoneFetchRequest();
    if (block_id) {
      req.block_ids.push(block_id);
    }
    let tdResponse = (await Village.FetchTransportData(
      req,
      errorHandler,
    )) as TransportData;
    return Village.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireListByFilters(
    zone_id: number,
    district_id: number,
    block_id: number,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler,
  ) {
    let req = new VillageWithoutZoneFetchRequest();
    if (zone_id) {
      req.zone_ids.push(zone_id);
    }
    if (district_id) {
      req.district_ids.push(district_id);
    }
    if (block_id) {
      req.block_ids.push(block_id);
    }
    let tdResponse = (await Village.FetchTransportData(
      req,
      errorHandler,
    )) as TransportData;
    return Village.ListFromTransportData(tdResponse);
  }

  public async DeleteInstance(
    successHandler: () => Promise<void> = null!,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler,
  ) {
    let tdRequest = new TransportData();
    tdRequest.RequestType = RequestTypes.Deletion;

    this.MergeIntoTransportData(tdRequest);
    let pktRequest =
      PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr =
      await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
    } else {
      if (!isNullOrUndefined(successHandler)) await successHandler();
    }
  }
}
